import { useState } from "react";
import { CollapsibleContent } from "@radix-ui/react-collapsible";

import { EditViewModal } from "components/saved_searches/EditViewModal";
import {
  generateSavedViewUrl,
  useSavedViewsGeneric,
  viewActionTooltip,
} from "components/saved_searches/utils";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { SidebarGroup, useOptionalSidebar } from "components/ui/sidebar/Sidebar";
import { useDeletePinnedView } from "lib/hooks/api/pinned_views/useDeletePinnedView";
import { useSavedViews } from "lib/hooks/api/saved_views/useSavedViews";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { IconProps } from "lib/icons/untitled_ui/UIcon";
import { SavedViewSortBy, SavedViewSortOrder } from "lib/types/graphQLEnums";
import { SavedViewDataTypeUnion, SavedViewUnion } from "lib/types/savedViews";
import {
  generateRegexForSavedViewUrl,
  NavItemButton,
  NavItemLink,
  NavItemLinkSplit,
  SidebarCollapsibleGroup,
  SidebarCollapsibleTrigger,
  SidebarMenuCollapsible,
  SidebarMenuIconCollapsible,
} from "./utils";

type PinnedGroupProps = {
  isGuestUser: boolean;
};

const PinnedGroup = ({ isGuestUser }: PinnedGroupProps) => {
  const [editModal, setEditModal] = useState<SavedViewUnion | null>(null);

  const { data: pinnedViews } = useSavedViews(
    {
      pinned: true,
      orderBy: SavedViewSortBy.PinnedRank,
      order: SavedViewSortOrder.Asc,
    },
    { page: 1, pageSize: 50 },
    { enabled: !isGuestUser },
  );

  return (
    <SidebarCollapsibleGroup>
      <SidebarGroup>
        <SidebarCollapsibleTrigger>Pinned</SidebarCollapsibleTrigger>
        <SidebarMenuIconCollapsible>
          <CollapsibleContent asChild>
            <SidebarMenuCollapsible>
              {pinnedViews.results.length === 0 && (
                <NavItemButton label="No pinned searches yet" icon="pin02" disabled />
              )}
              {pinnedViews.results.map((view) => (
                <NavItemPinned
                  key={view.id}
                  view={view}
                  setEditModal={setEditModal}
                  isGuestUser={isGuestUser}
                />
              ))}
            </SidebarMenuCollapsible>
          </CollapsibleContent>
        </SidebarMenuIconCollapsible>
        {!isGuestUser && (
          <EditViewModal
            isOpen={editModal !== null}
            selectedView={editModal}
            onOpenChange={(open) => {
              if (!open) {
                setEditModal(null);
              }
            }}
          />
        )}
      </SidebarGroup>
    </SidebarCollapsibleGroup>
  );
};

type NavItemPinnedProps = {
  view: SavedViewUnion;
  setEditModal: (view: SavedViewUnion | null) => void;
  isGuestUser: boolean;
};

const SEARCH_DATA_TYPE_ICON: Record<SavedViewDataTypeUnion, IconProps["icon"]> = {
  notice: "file02",
  document: "box",
  noticeSemantic: "lightning01",
};

const NavItemPinned = ({ view, setEditModal, isGuestUser }: NavItemPinnedProps) => {
  const { setSelectedView } = useSavedViewsGeneric();
  const { mutate: deletePinnedView, isLoading: isDeletingPinnedView } = useDeletePinnedView();

  const { data: user } = useCurrentUser();
  const isMobile = useOptionalSidebar()?.isMobile ?? false;

  const itemProps = {
    label: view.name,
    icon: SEARCH_DATA_TYPE_ICON[view.dataType],
    activeRegex: generateRegexForSavedViewUrl(view.id, view.dataType),
    href: generateSavedViewUrl(view.id, view.dataType),
    onClick: (e: React.MouseEvent) =>
      setSelectedView(view.id, view.dataType, e, view.name, "Vertical nav"),
    // Can't go on right due to dropdown button & adding extra padding puts it too far away
    tooltipProps: { side: "top" } as const,
  };

  // Mobile: no "..." menu — it's a hover affordance with no touch equivalent, and unpin/edit
  // aren't phone tasks. The plain (non-split) item also drops the space reserved for that action.
  if (isMobile) {
    return <NavItemLink {...itemProps} />;
  }

  return (
    <NavItemLinkSplit {...itemProps} secondaryType="button">
      {!isGuestUser && user && (
        <DropdownMenu
          modal={false}
          trigger={
            <DropdownMenuTrigger
              buttonStyle="inverse"
              buttonType="primary"
              icon="dotsHorizontal"
              aria-label="Show search options"
              hideArrow
            />
          }
        >
          <DropdownMenuItem
            icon="pin02Filled"
            onSelect={() => deletePinnedView({ input: { savedViewId: view.id } })}
            loading={isDeletingPinnedView}
          >
            Unpin
          </DropdownMenuItem>
          <DropdownMenuItem
            icon="edit02"
            onSelect={() => setEditModal(view)}
            disabled={view.isStandardView || view.owner?.guid !== user.guid}
            tooltip={viewActionTooltip("edit", view, user.guid)}
          >
            Edit
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    </NavItemLinkSplit>
  );
};

export default PinnedGroup;
